/**
 * Free-text handling for the DBS character set (PositionAppliedForType, OrganisationNameType and the address and
 * name types all share "A-Z 0-9 ( ) - / ' & and spaces", upper case, no leading or trailing space).
 */

/** The characters the DBS accepts in free-text fields, as a human-readable list for error messages. */
export const DBS_TEXT_ALLOWED_DESCRIPTION = "A-Z 0-9 ( ) - / ' & and spaces"

/** The same set as a character class. Transcribed from PositionAppliedForType / OrganisationNameType in the XSD. */
export const DBS_TEXT_ALLOWED_CHARACTER = /[A-Z0-9() \-/'&]/

/**
 * Word and Outlook silently substitute typographic characters as you type, and pasting from them is the commonest
 * way an invalid job role gets created (DBS-237). Each of these has an unambiguous character in the DBS set, so
 * correct them rather than bouncing the user.
 *
 * Characters with no valid equivalent (curly double quotes, ellipsis, accented letters) are left alone
 * deliberately, so validateDBSText can name them in the error.
 */
export const normaliseDBSText = (value: string): string =>
  value
    // hyphen, non-breaking hyphen, figure dash, en dash, em dash, horizontal bar
    .replace(/[‐-―]/g, '-')
    // curly single quotes, single high-reversed quote, prime
    .replace(/[‘’‛′]/g, "'")
    // non-breaking space, en/em/thin spaces, narrow + medium mathematical space, ideographic space
    .replace(/[  -   　]/g, ' ')

/**
 * Validate a value destined for a DBS free-text field. `value` must already be normalised, trimmed and upper-cased,
 * i.e. exactly what will be written into the XML.
 *
 * The checks are equivalent to the anchored XSD pattern: every character in the allowed set, and no leading or
 * trailing space. They are written out rather than as the regex so the message can name the offending character.
 *
 * @returns true if valid, otherwise the reason it was rejected.
 */
export const validateDBSText = (value: string, maxLength: number = 60): string | true => {
  if (value.length === 0) {
    return 'Must not be empty'
  }

  if (value.length > maxLength) {
    return `Too long: the DBS allows ${maxLength} characters and this is ${value.length}`
  }

  // Array.from rather than a spread: the portals target ES5 and would need downlevelIteration to spread a Set
  const invalidCharacters = Array.from(new Set(value.split('').filter(character => !DBS_TEXT_ALLOWED_CHARACTER.test(character))))
  if (invalidCharacters.length > 0) {
    // Annotate anything non-ASCII with its codepoint: an en dash is indistinguishable from a hyphen on screen, and a
    // stray zero-width space would otherwise be quoted as nothing at all.
    const described = invalidCharacters
      .map(character => {
        const codePoint = character.codePointAt(0) ?? 0
        return codePoint > 126 || codePoint < 32
          ? `"${character}" (U+${codePoint.toString(16).toUpperCase().padStart(4, '0')})`
          : `"${character}"`
      })
      .join(', ')
    return `${described} ${invalidCharacters.length === 1 ? 'is' : 'are'} not accepted by the DBS. Allowed: ${DBS_TEXT_ALLOWED_DESCRIPTION}`
  }

  if (value.startsWith(' ') || value.endsWith(' ')) {
    return 'Must not start or end with a space'
  }

  return true
}
